import { useMemo } from 'react'
import { QUESTION_TYPES } from '@commutifi-fe/interfaces'
import { CombinedModes } from '@commutifi-fe/constants'
import { Form, Question } from '@commutifi-fe/ui'
import { ModeCategory } from '@commutifi/modes'

import GoogleTransitDirections from 'shared/components/common/GoogleTransitDirections'
import { DayPicker, getDayPickerValidation } from 'shared/components/common/DayPicker'
import { CommuteModePicker } from 'shared/components/common/CommuteModePicker'
import { frequencyAttribute, routeAttribute } from 'constants/onboarding'
import { ComposedInputComponentProps } from 'shared/interfaces/Survey'
import { inferModeFromLegs, isValidCombinedMode } from 'utils/modes'
import { FormattedMessage, useIntl } from 'locales/index'
import { useDeepCompareEffect } from 'shared/hooks/global'
import { Frequency, Route } from 'shared/interfaces/Commute'
import { generateSubQuestionId } from 'utils/helpers/surveys'

import { multiModesQuestion, secondaryRouteModesQId, secondaryRouteQId } from './customQuestions'
import { ICommuteRouter, nextSubSection } from '.'

type CommuteRouterValueProp = Record<string, { route: Route; frequency: Frequency }>
export type SecondaryCommuteRouterProps = ComposedInputComponentProps<CommuteRouterValueProp> &
  ICommuteRouter & { availableFrequency: Frequency }

function SecondaryCommuteRouter({
  id,
  question,
  value = {},
  startPoint,
  endPoint,
  arrivalTime,
  form,
  upsertQuestions,
  validation,
  availableFrequency
}: SecondaryCommuteRouterProps) {
  const intl = useIntl()
  // Should look like commuteProfile_route.secondary.route
  const routeSubQuestionId = useMemo(() => generateSubQuestionId(question, routeAttribute), [question])
  // Should look like commuteProfile_route.secondary.frequency
  const routeFrequencySubQuestionId = useMemo(() => generateSubQuestionId(question, frequencyAttribute), [question])
  const currentRoute: Route = form.getFieldValue(routeSubQuestionId) || value[routeAttribute]
  const isPublicTransitOnlyRoute = useMemo(
    () => currentRoute && inferModeFromLegs(currentRoute.legs).category === ModeCategory.PublicTransit,
    [currentRoute]
  )

  // Add question to know the details about the user commute steps (commute timeline component)
  // when the user has a combined modes commute.
  // This question will depend on mode the user select on this question
  useDeepCompareEffect(() => {
    const routeFirstLeg = currentRoute?.legs?.[0] || {}
    if (!routeFirstLeg.category) {
      return
    }
    const section = nextSubSection(question.section)
    // Add a question to the next section when the user answers he uses
    // mixed modes of drive & transit
    const newQuestion = multiModesQuestion({
      // Use preset drive and transit commute timeline
      ...(routeFirstLeg.category === CombinedModes.DriveAndTransit && {
        type: QUESTION_TYPES.commuteTimelineDriveAndTransit
      }),
      id: secondaryRouteModesQId,
      section,
      // Needs to be the same as the form field decorator id so we can
      // prefill the section form with already answered questions
      referenceKey: routeSubQuestionId,
      conditionalQuestionId: secondaryRouteQId,
      enabled: isValidCombinedMode(routeFirstLeg.category),
      content: {
        title: intl.formatMessage({ id: 'commuteRoute.timeline.questions.secondaryRoute' })
      }
    })
    upsertQuestions(newQuestion)
  }, [currentRoute, routeSubQuestionId, question])

  return (
    <>
      <Form.Item
        name={routeSubQuestionId}
        {...validation}
        data-testid="commuteProfile_secondaryCommute"
        label={<Question title={question.content?.title} />}
      >
        <CommuteModePicker id={id} startPoint={startPoint} endPoint={endPoint} />
      </Form.Item>
      {/* If the user public transit we want to overwrite the question answers with more details coming from google api */}
      <Form.Item
        hidden={!isPublicTransitOnlyRoute}
        name={routeSubQuestionId}
        valuePropName="transitValue"
        {...validation}
        label={
          <Question
            title={<FormattedMessage id="commuteRoute.modes.transit.question" />}
            hint={<FormattedMessage id="commuteRoute.modes.transit.question.hint" />}
          />
        }
        data-testid="public-transit-suggestions"
      >
        <GoogleTransitDirections.CommutifiTransitRoute
          id={id}
          enabled={Boolean(isPublicTransitOnlyRoute)}
          startLocation={startPoint.location}
          startAddress={startPoint.formattedAddress}
          endLocation={endPoint.location}
          endAddress={endPoint.formattedAddress}
          arrivalTime={arrivalTime}
          timezone={endPoint.timezone}
        >
          <GoogleTransitDirections.OtherOption />
        </GoogleTransitDirections.CommutifiTransitRoute>
      </Form.Item>
      <Form.Item
        name={routeFrequencySubQuestionId}
        {...getDayPickerValidation(1, availableFrequency.weeklyFrequency)}
        data-testid="commuteProfile_secondaryCommuteFrequency"
        label={
          <Question
            title={<FormattedMessage id="commuteRoute.secondaryRoute.questions.days" />}
            hint={<FormattedMessage id="commuteRoute.secondaryRoute.questions.days.hint" />}
          />
        }
      >
        <DayPicker
          // We will always force the user to select the same type of frequency depending on the commute
          // profile frequency question.
          id={id}
          allowFrequency={false}
          initialIsSetDays={(availableFrequency.days || []).length > 0}
          daysSubset={availableFrequency.days}
          maxWeeklyFrequency={availableFrequency.weeklyFrequency}
        />
      </Form.Item>
    </>
  )
}

export default SecondaryCommuteRouter
